import {useRef} from 'react';

import {useLatest, useOnMount} from 'shared/web';

import {UploadWaitCancelled} from '../utils/UploadWaitCancelled';
import {useFileNotification} from './useFileNotification';

export function useWaitForUploadedFile() {
  const pendingRejectionsRef = useRef(new Map<string, (error: Error) => void>());

  const {registerFile, cancelFile} = useFileNotification({
    onTimeout: (fileId) => {
      pendingRejectionsRef.current.get(fileId)?.(
        new Error(`Timed out waiting for the uploaded file notification (fileId: ${fileId})`)
      );
      pendingRejectionsRef.current.delete(fileId);
    },
  });

  const cancelFileRef = useLatest(cancelFile);

  /**
   * `registerFile` arms a timeout that outlives the editor, so an abandoned upload would raise an
   * error notification half a minute later. Exposed as well as run on unmount, because the editor
   * can unmount while the form holding this hook stays mounted.
   */
  const cancelPendingWaits = () => {
    const pendingRejections = pendingRejectionsRef.current;
    pendingRejections.forEach((reject, fileId) => {
      cancelFileRef.current(fileId);
      reject(new UploadWaitCancelled(fileId));
    });
    pendingRejections.clear();
  };

  useOnMount(undefined, cancelPendingWaits);

  const waitForUploadedFile = (fileId: string) =>
    new Promise<void>((resolve, reject) => {
      pendingRejectionsRef.current.set(fileId, reject);
      registerFile(fileId, {
        onSuccess: () => {
          pendingRejectionsRef.current.delete(fileId);
          resolve();
        },
        onError: () => {
          pendingRejectionsRef.current.delete(fileId);
          reject(
            new Error(`The storage service failed to process the uploaded file (fileId: ${fileId})`)
          );
        },
      });
    });

  return {waitForUploadedFile, cancelPendingWaits};
}
